import { FunctionComponent } from 'react';

import styled from 'styled-components';

import { TabButton } from 'core/ui';

export interface ShareTabNavigationProps {
  selectedTab: 'Exams' | 'Files';
  onTabChange: (tab: 'Exams' | 'Files') => void;
  onFilterToggle?: () => void;
  isFilterExpanded?: boolean;
  selectedFilterName?: string;
  showFilesTab?: boolean;
  showExamsTab?: boolean;
}

export const ShareTabNavigation: FunctionComponent<ShareTabNavigationProps> = ({
  selectedTab,
  onTabChange,
  onFilterToggle,
  isFilterExpanded = false,
  selectedFilterName = 'All Files',
  showFilesTab = true,
  showExamsTab = true,
}) => {
  // Transform the display name for better UX - show "All Files" instead of "Filter" when no specific filter
  const displayName = selectedFilterName === 'All Files' || !selectedFilterName ? 'All Files' : selectedFilterName;
  return (
    <StyledTabsSection>
      <StyledTabsContainer>
        <StyledTabsWrapper>
          {showExamsTab && (
            <StyledTabButton selected={selectedTab === 'Exams'} roundedCorners="left" type="button" onClick={() => onTabChange('Exams')}>
              Exams
            </StyledTabButton>
          )}
          {showFilesTab && (
            <StyledTabButton
              selected={selectedTab === 'Files'}
              roundedCorners={showExamsTab ? 'right' : 'left'}
              type="button"
              onClick={() => onTabChange('Files')}
            >
              Files
            </StyledTabButton>
          )}
        </StyledTabsWrapper>
      </StyledTabsContainer>
    </StyledTabsSection>
  );
};

ShareTabNavigation.displayName = 'ShareTabNavigation';

const StyledTabsSection = styled.div`
  padding: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
  overflow-x: hidden; /* Prevent horizontal scrolling */

  @media (max-width: 768px) {
    margin: 0; /* Remove negative margins on mobile to prevent overflow */
    padding: 0;
  }
`;

const StyledTabsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start; /* Changed from center to flex-start to align left */
  padding: 0 2rem;
  gap: ${({ theme }) => theme.space.spacing20};
  overflow-x: hidden; /* Prevent horizontal scrolling */
  max-width: 100%; /* Ensure container doesn't exceed viewport */

  @media (max-width: 768px) {
    padding: 0 0.5rem; /* Reduced padding to prevent overflow */
    gap: ${({ theme }) => theme.space.spacing10};
    flex-wrap: nowrap; /* Prevent wrapping */
  }
`;

const StyledTabsWrapper = styled.div`
  display: flex;
  flex-shrink: 1; /* Allow tabs to shrink */
  min-width: 0; /* Allow tabs to shrink */

  @media (max-width: 768px) {
    flex: 1; /* Take remaining space after filter button */
  }
`;

const StyledTabButton = styled(TabButton)<{ selected: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.subheading} !important;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold} !important;
  padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing40} !important;
  min-height: 48px;

  && {
    background-color: transparent !important;
    border: none !important;
    border-bottom: 3px solid transparent !important;
    transition: all 0.2s ease;
    border-radius: 0 !important;
  }

  /* Selected state styling */
  ${({ selected, theme }) =>
    selected &&
    `
    && {
      border-bottom-color: ${theme.colors.primary} !important;
      color: ${theme.colors.primary} !important;
      font-weight: ${theme.fontWeights.bold} !important;
      background-color: transparent !important;
    }
  `}

  /* Non-selected state styling */
  ${({ selected, theme }) =>
    !selected &&
    `
    && {
      color: ${theme.colors.palette.grayscale[7]} !important;
      background-color: transparent !important;

      &:hover {
        color: ${theme.colors.palette.grayscale[9]} !important;
        border-bottom-color: ${theme.colors.palette.grayscale[5]} !important;
        background-color: ${theme.colors.palette.grayscale[1]} !important;
      }
    }
  `}

  /* Mobile adjustments */
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing20} ${({ theme }) => theme.space.spacing30} !important;
    font-size: ${({ theme }) => theme.fontSizes.body} !important;
    flex-shrink: 1; /* Allow tabs to shrink on mobile */
    min-width: 0; /* Allow tabs to shrink */
  }
`;

const StyledFilterButton = styled.button<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};
  background: ${({ theme }) => theme.colors.primary}; /* Always blue/primary color */
  color: ${({ theme }) => theme.colors.palette.white}; /* Always white text */
  border: 1px solid ${({ theme }) => theme.colors.primary}; /* Always primary border */
  border-radius: ${({ theme }) => theme.radii.base};
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 40px;
  margin-right: ${({ theme }) => theme.space.spacing20}; /* Changed from margin-left to margin-right since it's now on the left */

  &:hover {
    background: ${({ theme }) => theme.colors.palette.blues[7]}; /* Always darker blue on hover */
    border-color: ${({ theme }) => theme.colors.palette.blues[7]};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .icon-container {
    font-size: 14px;
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
    margin-right: ${({ theme }) => theme.space.spacing10};
    min-width: 44px; /* Ensure button is large enough for touch */
    justify-content: center; /* Center the icon when text is hidden */
  }
`;

const StyledFilterText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.body};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};

  @media (max-width: 768px) {
    display: none; /* Hide text on mobile, show only icon */
  }
`;
